/* eslint-disable @typescript-eslint/no-explicit-any */
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { ElementRef, ReactNode, forwardRef } from 'react';

import { cn } from 'styles';
import { TabType, useTab } from './context';

export interface TabsProps {
  value: string;
  disabled?: boolean;
  className?: string;
  children: ReactNode;

  modalTrigger?: boolean;

  tooltipContent?: ReactNode;

  /**
   * Disabled the default keyboard navigation (arrow keys etc)
   *
   * Useful if you are controlling the same keys for a specific part of the app
   *
   * Normally this should not be disabled
   */
  blockKeyboardNav?: boolean;

  onClick?: () => void;
}

interface TabListProps {
  className?: string;
  children: ReactNode;
}

type TypeConfig = Record<
  TabType,
  {
    list: string;
    tab: string;
    asModalTrigger: string;
  }
>;

const typeClasses: TypeConfig = {
  boxed: {
    list: 'rounded-md bg-muted text-muted-foreground px-1',

    tab: [
      'rounded-sm',
      'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
    ].join(' '),

    asModalTrigger: '',
  },

  highlighted: {
    list: '',
    tab: 'data-[state=active]:text-primary data-[state=active]:bg-primary/5 rounded-lg data-[state=inactive]:hover:bg-hover transition-colors',
    asModalTrigger: 'text-primary bg-primary/5',
  },

  underline: {
    list: 'text-muted-foreground gap-2',
    tab: 'relative data-[state=active]:text-primary',
    asModalTrigger: 'text-primary',
  },
};

export const TabsList = forwardRef<ElementRef<typeof TabsPrimitive.List>, TabListProps>(
  ({ className, ...props }, ref) => {
    const { type, fullWidth, contentFlow } = useTab();

    return (
      <TabsPrimitive.List
        ref={ref}
        className={cn(
          'inline-flex justify-center',
          {
            'w-full': fullWidth,
          },
          contentFlow === 'left-to-right' ? 'flex-col items-start gap-2' : 'h-10 items-center',
          typeClasses[type].list,
          className,
        )}
        {...props}
      />
    );
  },
);

TabsList.displayName = TabsPrimitive.List.displayName;

export const Tab = forwardRef<ElementRef<typeof TabsPrimitive.Trigger>, TabsProps>(
  ({ className, children, modalTrigger, blockKeyboardNav, ...props }, ref) => {
    const { type, fullWidth, value, contentFlow } = useTab();

    const Comp = modalTrigger ? 'button' : TabsPrimitive.Trigger;

    const classes = typeClasses[type];

    return (
      <Comp
        ref={ref}
        className={cn(
          'inline-flex items-center gap-2 whitespace-nowrap px-3 py-1.5 text-sm font-medium transition-all',
          'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50',
          classes.tab,
          {
            'flex-1': fullWidth,
          },
          contentFlow === 'left-to-right' ? 'w-full justify-start' : 'justify-center',
          modalTrigger && classes.asModalTrigger,
          className,
        )}
        {...props}
        onKeyDown={blockKeyboardNav ? (e) => e.preventDefault() : undefined}
      >
        {children}

        {type === 'underline' && value === props.value && (
          <span className="absolute bottom-[-2px] left-0 h-[2px] w-full rounded-full bg-primary" />
        )}
      </Comp>
    );
  },
);

Tab.displayName = 'Tab';
